import { Injectable } from '@angular/core';
import { DataService } from './dataservice.service';
import { DS_TAB_CONFIG, DsFieldDef, DsTabDef, isGroupDef, CUSTOMER_FIELD_WHITELIST, CUSTOMER_EXPORT_RULES } from './ds-export-config';
import * as XLSX from 'xlsx-js-style';
import { RevisionFieldDelta } from './revision-handler.service';

export interface DsExportHeaderInfo {
  formatNo: string;
  customerName: string;
  projectName: string;
  switchboard: string;
  abbReference: string;
}

export interface RevisionExportHeaderInfo extends DsExportHeaderInfo {
  currentRevision: string;
  generatedDate: string;
}

export interface RevisionExportSummary {
  totalRevisions: number;
  totalFieldsModified: number;
  totalImpactedSections: number;
  latestRevision: string;
  generatedBy: string;
  generatedDate: string;
}

export interface RevisionExportPayload {
  summary: RevisionExportSummary;
  deltas: RevisionFieldDelta[];
}

@Injectable({
  providedIn: 'root'
})
export class DsExportService {
  private readonly HEADER_BG = 'D2693D';
  private readonly SECTION_BG = 'FFD3AC';
  private readonly THIN_BORDER = {
    top: { style: 'thin', color: { rgb: '000000' } },
    bottom: { style: 'thin', color: { rgb: '000000' } },
    left: { style: 'thin', color: { rgb: '000000' } },
    right: { style: 'thin', color: { rgb: '000000' } }
  };
  private _lastDsData: any;
  constructor(private _dataService: DataService) { }

  exportDatasheet(headerInfo: DsExportHeaderInfo, dsData: any): void {
    this.doExportDatasheet(headerInfo, dsData, DS_TAB_CONFIG, 'Datasheet_', false);
  }

  exportForCustomer(headerInfo: DsExportHeaderInfo, dsData: any): void {
    const filteredConfig = this.getFilteredTabConfigForCustomer();
    this.doExportDatasheet(headerInfo, dsData, filteredConfig, 'Datasheet_ForCustomer_', true);
  }

  exportRevisionSummary(headerInfo: RevisionExportHeaderInfo, payload: RevisionExportPayload): void {
    const rows: any[][] = [];
    const styles: { [cell: string]: any } = {};
    const deltas = Array.isArray(payload?.deltas) ? payload.deltas : [];
    const generatedHeaderDate = this.formatRevisionDateTime(new Date());
    const generatedSummaryDate = this.formatRevisionDateTime(new Date());

    rows.push(['Format No', headerInfo.formatNo, 'Customer Name', headerInfo.customerName]);
    rows.push(['Project', headerInfo.projectName, 'Switchboard', headerInfo.switchboard]);
    rows.push(['ABB Reference', headerInfo.abbReference, 'Current Revision', headerInfo.currentRevision]);
    rows.push(['Generated Date', generatedHeaderDate, '', '']);
    rows.push([]);

    for (let r = 0; r < 4; r++) {
      this.setStyle(styles, r, 0, { font: { bold: true, sz: 10 } });
      this.setStyle(styles, r, 2, { font: { bold: true, sz: 10 } });
    }

    rows.push(['', 'EXECUTIVE SUMMARY', '', '']);
    const summaryHdrIdx = rows.length - 1;
    for (let c = 0; c < 4; c++) {
      this.setStyle(styles, summaryHdrIdx, c, {
        font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
        fill: { fgColor: { rgb: this.HEADER_BG } },
        border: this.THIN_BORDER
      });
    }

    rows.push(['Total Revisions', payload.summary.totalRevisions, 'Latest Revision', payload.summary.latestRevision]);
    rows.push(['Total Fields Modified', payload.summary.totalFieldsModified, 'Generated By', payload.summary.generatedBy]);
    rows.push(['Total Impacted Sections', payload.summary.totalImpactedSections, 'Generated Date', generatedSummaryDate]);
    rows.push([]);

    rows.push(['', 'DETAILED CHANGE REPORT', '', '']);
    const detailTitleIdx = rows.length - 1;
    for (let c = 0; c < 4; c++) {
      this.setStyle(styles, detailTitleIdx, c, {
        font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
        fill: { fgColor: { rgb: this.HEADER_BG } },
        border: this.THIN_BORDER
      });
    }

    rows.push(['S.No', 'Revision', 'Section', 'Field Name', 'Old Value', 'New Value']);
    const detailHdrIdx = rows.length - 1;
    for (let c = 0; c < 6; c++) {
      this.setStyle(styles, detailHdrIdx, c, {
        font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 10 },
        fill: { fgColor: { rgb: this.HEADER_BG } },
        alignment: { vertical: 'center', horizontal: 'center' },
        border: this.THIN_BORDER
      });
    }

    let serial = 1;
    for (const delta of deltas) {
      rows.push([
        serial,
        delta.revision || '',
        delta.section || '',
        delta.fieldLabel || delta.field || '',
        this.toRevisionDisplayValue(delta, delta.oldValue),
        this.toRevisionDisplayValue(delta, delta.newValue)
      ]);
      const rowIdx = rows.length - 1;
      for (let c = 0; c < 6; c++) {
        this.setStyle(styles, rowIdx, c, { font: { sz: 10 }, border: this.THIN_BORDER });
      }
      serial++;
    }

    rows.push([]);
    rows.push(['', 'SECTION WISE SUMMARY', '', '']);
    const swsTitleIdx = rows.length - 1;
    for (let c = 0; c < 4; c++) {
      this.setStyle(styles, swsTitleIdx, c, {
        font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
        fill: { fgColor: { rgb: this.HEADER_BG } },
        border: this.THIN_BORDER
      });
    }

    const grouped = this.groupDeltasBySection(deltas);
    let secNo = 1;
    Object.keys(grouped).forEach(section => {
      rows.push([String(secNo), section, '', '']);
      const secRow = rows.length - 1;
      for (let c = 0; c < 4; c++) {
        this.setStyle(styles, secRow, c, {
          font: { bold: true, sz: 10 },
          fill: { fgColor: { rgb: this.SECTION_BG } },
          border: this.THIN_BORDER
        });
      }

      const sectionDeltas = grouped[section];
      let fieldNo = 1;
      for (const d of sectionDeltas) {
        const oldV = this.toRevisionDisplayValue(d, d.oldValue);
        const newV = this.toRevisionDisplayValue(d, d.newValue);
        rows.push([
          `${secNo}.${fieldNo}`,
          d.fieldLabel || d.field || '',
          `${oldV} -> ${newV}`,
          d.revision || ''
        ]);
        const dr = rows.length - 1;
        for (let c = 0; c < 4; c++) {
          this.setStyle(styles, dr, c, { font: { sz: 10 }, border: this.THIN_BORDER });
        }
        fieldNo++;
      }
      rows.push([]);
      secNo++;
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    Object.keys(styles).forEach(ref => {
      if (!ws[ref]) { ws[ref] = { t: 's', v: '' }; }
      ws[ref].s = styles[ref];
    });

    ws['!cols'] = [
      { wch: 20 },
      { wch: 28 },
      { wch: 28 },
      { wch: 22 },
      { wch: 22 },
      { wch: 22 }
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Revision Summary');
    const wbOut = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob(
      [wbOut],
      { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
    );

    const fileName =
      'Revision_Info_' +
      (headerInfo.abbReference || 'export').replace(/[^a-zA-Z0-9\-_]/g, '_') +
      '.xlsx';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }

  private applyCustomerRules(
    tab: DsTabDef,
    item: any,
    isCustomerExport: boolean
  ) {
    if (!isCustomerExport) return item;
    const rules = CUSTOMER_EXPORT_RULES[tab.sectionKey];
    if (!rules) return item;
    // GROUP HANDLING
    if (isGroupDef(item)) {
      const allowedGroupFields = rules.allowedGroups?.[item.groupLabel];
      if (allowedGroupFields) {
        return {
          ...item,
          fields: item.fields.filter(f => allowedGroupFields.includes(f.label)),
          arrayFields: item.arrayFields
            ? item.arrayFields.filter(f => allowedGroupFields.includes(f.label))
            : item.arrayFields
        };
      }
      return null;
    }

    //  SIMPLE FIELD
    if (rules.allowedFields) {
      return rules.allowedFields.includes(item.label) ? item : null;
    }
    return item;
  }

  private doExportDatasheet(
    headerInfo: DsExportHeaderInfo,
    dsData: any,
    tabConfig: DsTabDef[],
    filePrefix: string,
    isCustomerExport: boolean = false
  ): void {
    this._lastDsData = dsData;
    const rows: any[][] = [];
    const styles: { [cell: string]: any } = {};
    // Header rows
    rows.push(['Format No:', headerInfo.formatNo, 'Customer Name:', headerInfo.customerName]);
    rows.push(['Project:', headerInfo.projectName, 'Switchboard:', headerInfo.switchboard]);
    rows.push(['ABB Reference:', headerInfo.abbReference, '', '']);
    rows.push([]);

    for (let r = 0; r < 3; r++) {
      this.setStyle(styles, r, 0, { font: { bold: true, sz: 10 } });
      if (r < 2) { this.setStyle(styles, r, 2, { font: { bold: true, sz: 10 } }); }
    }

    // Column headers
    rows.push(['S.No.', 'Description', 'Parameters', 'Unit/Remark']);
    const hdrRowIdx = rows.length - 1;
    for (let c = 0; c < 4; c++) {
      this.setStyle(styles, hdrRowIdx, c, {
        font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
        fill: { fgColor: { rgb: this.HEADER_BG } },
        alignment: { vertical: 'center', horizontal: 'center' },
        border: this.THIN_BORDER
      });
    }

    // Data rows
    let mainCounter = 0;
    for (const tab of tabConfig) {
      mainCounter++;
      const tabNum = mainCounter;
      this.pushSectionRow(rows, styles, String(tabNum), tab.tabName);
      // SIMPLE & CLEAN
      if (tab.isStatic && tab.staticLines) {
        for (let i = 0; i < tab.staticLines.length; i++) {
          this.pushDataRow(
            rows,
            styles,
            tabNum + '.' + (i + 1),
            tab.staticLines[i],
            '',
            ''
          );
        }
      } else {
        const tabData = dsData[tab.sectionKey];
        let subCounter = 0;

        for (let item of tab.items) {
          // APPLY CUSTOMER RULES
          item = this.applyCustomerRules(tab, item, isCustomerExport);
          if (!item) continue;
          if (isGroupDef(item)) {
            subCounter++;
            const groupNum = subCounter;
            this.pushGroupHeaderRow(rows, styles, tabNum + '.' + groupNum, item.groupLabel);
            // REPEATING GROUP
            if (item.isRepeating && item.arrayKey && item.arrayFields) {
              const arr: any[] = (tabData && tabData[item.arrayKey]) || [];
              if (arr.length === 0) {
                for (let ai = 0; ai < item.arrayFields.length; ai++) {
                  this.pushDataRow(
                    rows,
                    styles,
                    tabNum + '.' + groupNum + '.' + (ai + 1),
                    item.arrayFields[ai].label,
                    '',
                    item.arrayFields[ai].unit || ''
                  );
                }
              } else {
                // LIMIT ROWS BASED ON CUSTOMER RULES
                let maxRows = arr.length;
                if (isCustomerExport) {
                  const rules = CUSTOMER_EXPORT_RULES[tab.sectionKey];
                  if (rules?.limitRowsToOne?.includes(item.groupLabel)) {
                    maxRows = Math.min(arr.length, 1);
                  }
                }

                for (let ri = 0; ri < maxRows; ri++) {
                  for (let ai = 0; ai < item.arrayFields.length; ai++) {
                    const val = this.getFieldValue(arr[ri], item.arrayFields[ai]);
                    this.pushDataRow(
                      rows,
                      styles,
                      tabNum + '.' + groupNum + '.' + (ri + 1) + '.' + (ai + 1),
                      item.arrayFields[ai].label,
                      val.param,
                      val.unitRemark
                    );
                  }
                }
              }
            } else {
              for (let fi = 0; fi < item.fields.length; fi++) {
                const val = this.getFieldValue(tabData, item.fields[fi]);
                this.pushDataRow(
                  rows,
                  styles,
                  tabNum + '.' + groupNum + '.' + (fi + 1),
                  item.fields[fi].label,
                  val.param,
                  val.unitRemark
                );
              }
            }
          } else {
            subCounter++;
            const val = this.getFieldValue(tabData, item);
            this.pushDataRow(
              rows,
              styles,
              tabNum + '.' + subCounter,
              item.label,
              val.param,
              val.unitRemark
            );
          }
        }

        // Notes
        if (tabData) {
          subCounter++;
          this.pushDataRow(rows, styles, tabNum + '.' + subCounter, 'Notes', '', '');
          this.pushDataRow(rows, styles, tabNum + '.' + subCounter + '.1', 'PM Note', tabData.sectionNotePM || '', '');
          this.pushDataRow(rows, styles, tabNum + '.' + subCounter + '.2', 'EE Note', tabData.sectionNoteEE || '', '');
          this.pushDataRow(rows, styles, tabNum + '.' + subCounter + '.3', 'ME Note', tabData.sectionNoteME || '', '');
        }
      }
      rows.push([]);
    }

    // Build worksheet
    const ws = XLSX.utils.aoa_to_sheet(rows);
    Object.keys(styles).forEach(ref => {
      if (!ws[ref]) { ws[ref] = { t: 's', v: '' }; }
      ws[ref].s = styles[ref];
    });

    ws['!cols'] = [
      { wch: 10 },
      { wch: 55 },
      { wch: 35 },
      { wch: 25 }
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Technical Data Sheet');
    const wbOut = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob(
      [wbOut],
      { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
    );

    const fileName =
      filePrefix +
      (headerInfo.abbReference || 'export').replace(/[^a-zA-Z0-9\-_]/g, '_') +
      '.xlsx';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }


  private getFilteredTabConfigForCustomer(): DsTabDef[] {
    return DS_TAB_CONFIG.map(tab => {
      const whitelist = CUSTOMER_FIELD_WHITELIST[tab.sectionKey];
      // Skip sections not in whitelist
      if (!whitelist) {
        return null;
      }
      // STATIC → return as-is
      if (tab.isStatic) {
        return tab;
      }
      const filteredItems = tab.items.map(item => {
        // HANDLE GROUPS
        if (isGroupDef(item)) {
          const filteredFields = item.fields.filter(f =>
            whitelist[f.label]
          );
          // IMPORTANT: HANDLE REPEATING GROUPS
          if (item.isRepeating && item.arrayFields) {
            const filteredArrayFields = item.arrayFields.filter(f =>
              whitelist[f.label] || whitelist[item.groupLabel]
            );
            // KEEP group if either has fields OR arrayFields
            if (filteredFields.length > 0 || filteredArrayFields.length > 0) {
              return {
                ...item,
                fields: filteredFields,
                arrayFields: filteredArrayFields
              };
            }
            return null;
          }

          // Normal group
          if (filteredFields.length > 0) {
            return {
              ...item,
              fields: filteredFields
            };
          }
          return null;
        }

        // HANDLE SIMPLE FIELD
        return whitelist[item.label] ? item : null;
      }).filter(Boolean);

      return {
        ...tab,
        items: filteredItems
      };
    }).filter(Boolean) as DsTabDef[];
  }

  private groupDeltasBySection(deltas: RevisionFieldDelta[]): { [section: string]: RevisionFieldDelta[] } {
    const grouped: { [section: string]: RevisionFieldDelta[] } = {};
    for (const d of deltas) {
      const key = d.section || 'Unknown';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(d);
    }
    return grouped;
  }

  private toRevisionDisplayValue(delta: RevisionFieldDelta, rawValue: string): string {
    if (!rawValue) return '';
    const fieldDef = this.findFieldDefByPath(delta.fieldPath);
    if (!fieldDef || !fieldDef.optionListKey) return rawValue;

    const resolved = this.resolveOption(fieldDef.optionListKey, rawValue);
    return resolved || rawValue;
  }

  private findFieldDefByPath(fieldPath: string): DsFieldDef | null {
    if (!fieldPath) return null;
    const match = fieldPath.match(/^([^.]+)\.(.+)$/);
    if (!match) return null;

    const sectionKey = match[1];
    const remaining = match[2];
    const fieldKey = remaining.split('.').pop() || '';
    const tab = DS_TAB_CONFIG.find(t => t.sectionKey === sectionKey);
    if (!tab) return null;

    for (const item of tab.items) {
      if (isGroupDef(item)) {
        if (item.isRepeating && item.arrayFields && item.arrayFields.length > 0) {
          const arrField = item.arrayFields.find(f => f.key === fieldKey);
          if (arrField) return arrField;
        }
        const groupField = item.fields.find(f => f.key === fieldKey);
        if (groupField) return groupField;
      } else if (item.key === fieldKey) {
        return item;
      }
    }

    return null;
  }

  private isFieldWhitelisted(label: string, whitelist: { [label: string]: boolean }): boolean {
    return whitelist[label] === true;
  }

  private resolveOption(optionListKey: string, optionId: string): string {
    if (!optionId || optionId === '' || optionId === 'NA' || optionId === '-') {
      return '';
    }

    const list: any[] = (this._dataService as any)[optionListKey];
    if (!list || !Array.isArray(list)) {
      return '';
    }

    const match = list.find((o: any) =>
      o.optionId === optionId ||
      o.yesNoId === optionId ||
      o.applicId === optionId ||
      o.reqNonReqId === optionId ||
      o.breakerPSId === optionId ||
      o.id === optionId
    );

    if (!match) {
      return '';
    }

    return (
      match.optionDisplayName ||
      match.optionName ||
      match.name ||
      match.yesNoName ||
      match.applicName ||
      match.reqNonReqName ||
      match.breakerPSName ||
      ''
    );
  }

  private getFieldValue(
    data: any,
    field: DsFieldDef
  ): { param: string; unitRemark: string } {
    if (!data) {
      return { param: '', unitRemark: '' };
    }
    let rawValue: any;

    // Main Busbar special handling
    if (field.label === 'Main Busbar (Amp)') {
      rawValue = this._lastDsData?.dsGeneralIP?.switchboardCurrRating;
      return {
        param: rawValue || '',
        unitRemark: rawValue ? 'A' : ''
      };
    }
    // Normal value
    rawValue = data[field.key];
    if (rawValue === undefined || rawValue === null || rawValue === '') {
      return { param: '', unitRemark: '' };
    }

    // Date formatting (When field)
    if (field.label === 'When' && rawValue) {
      const dateValue = new Date(rawValue);
      if (!isNaN(dateValue.getTime())) {
        const formatted = this.formatDate(dateValue);
        return { param: formatted, unitRemark: '' };
      }
    }

    let displayValue = String(rawValue).trim();

    if (displayValue === '') {
      return { param: '', unitRemark: '' };
    }

    // Resolve dropdown values
    if (field.optionListKey) {
      const resolved = this.resolveOption(field.optionListKey, displayValue);

      if (resolved) {
        displayValue = resolved;
      } else {
        return { param: '', unitRemark: '' };
      }
    }

    // Special case
    if (field.label.includes('Spring Charging Motor')) {
      return { param: displayValue, unitRemark: 'Watts' };
    }

    // Split value + unit and convert 'm' → 'meter'
    const match = displayValue.match(/^([><=]*\s*\d+(?:\.\d+)?)\s*([a-zA-Z°]+)$/);

    if (match) {
      let value = match[1].trim();
      let unit = match[2].trim();
      if (unit === 'm') {
        unit = 'meter';
      }
      return {
        param: value,
        unitRemark: unit
      };
    }

    // handle unit from config
    let unit = field.unit || '';

    if (unit === 'm') {
      unit = 'meter';
    }

    return {
      param: displayValue,
      unitRemark: displayValue ? unit : ''
    };
  }

  private formatDate(date: Date): string {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = date.getDate();
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  return `${month} ${day} ${year}`;
}

  private formatRevisionDateTime(value: string | Date): string {
    if (!value) return '';
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) {
      return String(value);
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hour24 = date.getHours();
    const minute = String(date.getMinutes()).padStart(2, '0');
    const ampm = hour24 >= 12 ? 'PM' : 'AM';
    const hour12 = String((hour24 % 12) || 12).padStart(2, '0');
    return `${year}-${month}-${day} ${hour12}:${minute}${ampm}`;
  }

  private pushSectionRow(rows: any[][], styles: any, sNo: string, description: string): void {
    rows.push([sNo, description, '', '']);
    const r = rows.length - 1;
    for (let c = 0; c < 4; c++) {
      this.setStyle(styles, r, c, {
        font: { bold: true, sz: 11 },
        fill: { fgColor: { rgb: this.SECTION_BG } },
        border: this.THIN_BORDER
      });
    }
  }


  private pushGroupHeaderRow(rows: any[][], styles: any, sNo: string, description: string): void {
    rows.push([sNo, description, '', '']);
    const r = rows.length - 1;
    for (let c = 0; c < 4; c++) {
      this.setStyle(styles, r, c, { font: { bold: true, sz: 10 }, border: this.THIN_BORDER });
    }
  }

  private pushDataRow(rows: any[][], styles: any, sNo: string, description: string, param: string, unitRemark: string): void {
    rows.push([sNo, description, param, unitRemark]);
    const r = rows.length - 1;
    for (let c = 0; c < 4; c++) {
      this.setStyle(styles, r, c, { font: { sz: 10 }, border: this.THIN_BORDER });
    }
  }

  private setStyle(styles: any, row: number, col: number, style: any): void {
    styles[XLSX.utils.encode_cell({ r: row, c: col })] = style;
  }
}
